/**
 * CRM App Component
 *
 * Lead CRM module root. Integrated into SiteOperations.io — uses the shared
 * AuthContext. ProtectedRoute mirrors the platform pattern: super_admins bypass;
 * everyone else needs the `crm` tool available via /api/my-tools. CRMProvider
 * lives in App.tsx so state persists across navigation.
 *
 * Shell layout: slim sticky app bar (with a mobile hamburger), then a flex row
 * of the collapsible <Sidebar> (desktop column / mobile drawer) and the routed
 * page. IA: / = Today worklist, /inbox = lead triage, /active + /awaiting =
 * conversation views, /by-study = kanban, /database = full patient list, and
 * /settings/* = the config hub (studies, forms, flows, templates, AI prompts,
 * release wording, system) — old top-level paths redirect there.
 * SoftphoneBar mounts once so calls survive CRM navigation.
 *
 * Typography: the CRM renders in self-hosted Inter (imported below, applied
 * via the `font-inter` wrapper) without affecting the other platform modules.
 */
import { Component, ErrorInfo, ReactNode, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';
import '@fontsource-variable/inter';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from '../theme/ThemeToggle';
import api from '../services/api';
import { ConnectionStatus, Contacts, ContactDetail, Conversations, ByStudy, Today, Calendar, StudiesList, StudyDetail, Reports, FormsList, FormBuilder, FlowsList, FlowBuilder, FaxConsole, PhoneConsole, PromptsList, PromptEditor, GlobalTemplates, ReleaseContent, RegistryBackfill, ProfileKeysLibrary, InterestListsSettings, ClinicianPortalAdmin, SafetyNet, SearchHome, SearchBuilder, SearchRun, AgentConsole, AgentSettings, BookingSettings, Help } from './pages';
import EligibilityReviewResults from './pages/EligibilityReviewResults';
import { SoftphoneProvider, useSoftphone } from './contexts/SoftphoneContext';
import { CallWorkspaceProvider } from './contexts/CallWorkspaceContext';
import { ParkedCallsPanel } from './components';
import { ConfirmProvider, Z } from './components/ui';
// The overlay manager: one bottom-right stack holding the call dock and every
// notification card. The softphone mounts inside it (variant="stack").
import OverlayHost from './components/overlay/OverlayHost';
import PresenceBeacon from './components/PresenceBeacon';
import CallWorkspace from './components/callmode/CallWorkspace';
import Sidebar from './components/Sidebar';
import MobileTabBar from './components/MobileTabBar';
import useNavCounts, { NavCountsContext } from './useNavCounts';
import { WorkQueueProvider } from './WorkQueueContext';
import NextUpBar from './components/NextUpBar';
import ToolNav from '../ui/ToolNav';
import SettingsLayout from './pages/settings/SettingsLayout';
import AdCampaignMapping from './pages/settings/AdCampaignMapping';
import { rememberLeadOrigin } from './lib';

// Keep a CRM render error from white-screening the rest of the app shell.
class CRMErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[CRM] render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center p-8 max-w-md">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Something went wrong in the CRM.</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Try reloading. The rest of the platform is unaffected.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    const check = async () => {
      // Super admins always have access (tool is "Coming Soon" to others in Phase 1)
      if (user?.role === 'super_admin') {
        setAllowed(true);
        setChecking(false);
        return;
      }
      try {
        const res = await api.get('/api/my-tools');
        const crmTool = (res.data || []).find((t: any) => t.tool_id === 'crm');
        setAllowed(crmTool?.is_available === true);
      } catch (err) {
        console.error('[CRM] entitlement check failed:', err);
        setAllowed(false);
      } finally {
        setChecking(false);
      }
    };
    if (session) {
      check();
    } else {
      setChecking(false);
    }
  }, [session, user?.role]);

  if (checking) return <Spinner />;
  if (!session) return <Navigate to="/login" replace />;

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to the CRM. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-150"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function CRMLayout({ children }: { children: ReactNode }) {
  const { inCall, hangup } = useSoftphone();
  const navigate = useNavigate();
  const location = useLocation();
  // Leaving the CRM ends any active call — one modal guards every exit (logo,
  // Tool Hub link, ToolNav switcher). `leaveTo !== null` is the "show modal"
  // condition; it holds the pending target.
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Nav badge counts, fetched once here and shared by the sidebar, the mobile
  // bottom tab bar, and — through NavCountsContext below — any page that wants
  // one of these numbers (see useNavCounts). Pages read the context instead of
  // fetching: two intervals against one endpoint is how Today's collision card
  // ended up polling patient names in parallel with the badge.
  const navCounts = useNavCounts();
  // The lead conversation (/crm/contacts/:id) is a focused chat screen on
  // phones — the tab bar yields there so the thread + keyboard get the room
  // (the back button returns to a tabbed list). Everywhere else the bar gives
  // one-thumb access to Today / Messages / Leads / Reports.
  const showTabBar = !location.pathname.startsWith('/crm/contacts/');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Slim single-line app bar (h-12): sticky so navigation is always in
          reach without eating the vertical space the old two-line banner did.
          The sidebar's sticky offset and the shell height calc both assume
          this 3rem height — keep them in sync. */}
      <header className={`sticky top-0 ${Z.header} bg-gradient-to-r from-primary-600 to-primary-700`}>
        <div className="flex h-12 items-center gap-2 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
              className="lg:hidden -ml-2 inline-flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 sm:h-9 sm:w-9 items-center justify-center rounded-lg text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link
              to="/"
              onClick={(e) => { if (inCall) { e.preventDefault(); setLeaveTo('/'); } }}
              aria-label="SiteOperations.io — Tool Hub"
              className="flex min-w-0 flex-shrink-0 items-center gap-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <img src="/pilllogo.svg" alt="" className="h-6 w-auto flex-shrink-0 brightness-0 invert" />
              <span className="hidden truncate text-[15px] font-semibold leading-none text-white sm:inline">
                SiteOperations.io
              </span>
            </Link>
            <span className="hidden text-white/40 sm:inline" aria-hidden="true">|</span>
          </div>
          {/* Cross-tool navigation. Leaving the CRM ends any active call, so route
              each jump through the same confirm modal the Tool Hub link uses. */}
          <ToolNav onNavigate={(to, e) => { if (inCall) { e.preventDefault(); setLeaveTo(to); } }} />
          {/* Leaving the CRM unmounts the softphone and ends any active call —
              guard the only in-app exit with a confirm modal while on a call.
              (Full-page unloads are guarded via beforeunload in SoftphoneContext.) */}
          <div className="flex flex-shrink-0 items-center gap-1">
            <ThemeToggle variant="onColor" />
            <Link
              to="/"
              onClick={(e) => { if (inCall) { e.preventDefault(); setLeaveTo('/'); } }}
              className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-sm font-medium text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60"
            >
              <ArrowLeft className="h-4 w-4" />
              Tool Hub
            </Link>
          </div>
        </div>
      </header>

      {leaveTo !== null && (
        /* modalRaised: this confirm must cover the softphone dock (z-50) and any
           open standard modal — it is raised from the app bar mid-call. */
        <div className={`fixed inset-0 ${Z.modalRaised} flex items-center justify-center bg-black/50 p-4`}>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">End the call and leave?</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              You're on an active call. Leaving the CRM will end it.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setLeaveTo(null)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Stay on call
              </button>
              <button
                onClick={() => {
                  hangup();
                  const to = leaveTo;
                  setLeaveTo(null);
                  if (to) navigate(to);
                }}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700"
              >
                Leave &amp; end call
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-stretch min-h-[calc(100vh-3rem)]">
        <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} counts={navCounts} />
        {/* Mobile bottom padding clears the bottom tab bar (3.5rem + safe area)
            and nothing else: the one overlay stack floats above the dock and is
            absent most of the day, so reserving a 10.5rem strip for it — as the
            five separate hosts once needed — was a permanent cost for an
            occasional card. */}
        <main
          className={`min-w-0 flex-1 px-4 pt-4 sm:px-6 lg:px-8 lg:pt-5 lg:pb-6 ${
            showTabBar ? 'pb-[calc(4.5rem+env(safe-area-inset-bottom))]' : 'pb-6'
          }`}
        >
          <div className="mx-auto w-full max-w-[1700px]">
            {/* Pages read the shell's badge counts from here rather than opening
                a poll of their own against the same endpoint. */}
            <NavCountsContext.Provider value={navCounts}>{children}</NavCountsContext.Provider>
          </div>
        </main>
      </div>

      {showTabBar && <MobileTabBar counts={navCounts} onMore={() => setMobileNavOpen(true)} />}
    </div>
  );
}

/** Every CRM page renders behind the entitlement check inside the shared shell. */
function page(el: ReactNode) {
  return (
    <ProtectedRoute>
      <CRMLayout>{el}</CRMLayout>
    </ProtectedRoute>
  );
}

/** Settings-hub list pages additionally render inside the sub-nav shell. */
function settings(el: ReactNode) {
  return page(<SettingsLayout>{el}</SettingsLayout>);
}

/** Legacy-bookmark redirect that preserves route params and the query string. */
function ParamRedirect({ build }: { build: (params: Readonly<Record<string, string | undefined>>) => string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={`${build(params)}${search}`} replace />;
}

function CRMRoutes() {
  const location = useLocation();
  // Record every non-lead CRM location so ContactDetail's back button returns
  // to the page the lead was actually opened from (see rememberLeadOrigin).
  useEffect(() => {
    if (!location.pathname.startsWith('/crm/contacts/')) {
      rememberLeadOrigin(`${location.pathname}${location.search}`);
    }
  }, [location]);
  return (
    <Routes>
      {/* The daily worklist is the landing page: "who do I contact today?" */}
      <Route path="/" element={page(<Today />)} />
      <Route path="/calendar" element={page(<Calendar />)} />
      <Route path="/inbox" element={page(<Contacts mode="inbox" />)} />
      {/* Old bookmark target — contacts triage now lives at /crm/inbox */}
      <Route path="/contacts" element={<Navigate to="/crm/inbox" replace />} />
      <Route path="/active" element={page(<Conversations mode="active" />)} />
      <Route path="/awaiting" element={page(<Conversations mode="awaiting" />)} />
      <Route path="/by-study" element={page(<ByStudy />)} />
      <Route path="/database" element={page(<Contacts mode="database" />)} />
      {/* Search tab — funnel cohort builder over the whole lead database.
          Static 'runs' segment outranks the :id param in v6 route ranking. */}
      <Route path="/safety-net" element={page(<SafetyNet />)} />
      <Route path="/search" element={page(<SearchHome />)} />
      <Route path="/search/new" element={page(<SearchBuilder />)} />
      <Route path="/search/runs/:runId" element={page(<SearchRun />)} />
      <Route path="/search/:id" element={page(<SearchBuilder />)} />
      {/* Texting-agent console — board / draft queue / escalations (§7.1). */}
      <Route path="/agent" element={page(<AgentConsole />)} />
      {/* Settings hub — every config surface under one roof with a sub-nav.
          List pages get the SettingsLayout rail; the full-page builders
          (study hub, form/flow/prompt editors) stay full-width. */}
      <Route path="/settings" element={<Navigate to="/crm/settings/studies" replace />} />
      <Route path="/settings/studies" element={settings(<StudiesList />)} />
      <Route path="/settings/studies/:id" element={page(<StudyDetail />)} />
      <Route path="/settings/forms" element={settings(<FormsList />)} />
      <Route path="/settings/forms/:id" element={page(<FormBuilder />)} />
      <Route path="/settings/profile-keys" element={settings(<ProfileKeysLibrary />)} />
      <Route path="/settings/interest-lists" element={settings(<InterestListsSettings />)} />
      <Route path="/settings/flows" element={settings(<FlowsList />)} />
      <Route path="/settings/flows/:id" element={page(<FlowBuilder />)} />
      <Route path="/settings/templates" element={settings(<GlobalTemplates />)} />
      <Route path="/settings/prompts" element={settings(<PromptsList />)} />
      <Route path="/settings/ad-campaigns" element={settings(<AdCampaignMapping />)} />
      <Route path="/settings/prompts/:key" element={page(<PromptEditor />)} />
      <Route path="/settings/release-content" element={settings(<ReleaseContent />)} />
      <Route path="/settings/registry-backfill" element={settings(<RegistryBackfill />)} />
      <Route path="/settings/clinician-portal" element={settings(<ClinicianPortalAdmin />)} />
      <Route path="/settings/agent" element={settings(<AgentSettings />)} />
      <Route path="/settings/booking" element={settings(<BookingSettings />)} />
      <Route path="/settings/system" element={settings(<ConnectionStatus />)} />
      {/* Old bookmark targets — the config pages moved under /crm/settings.
          Param + query preserved (the backend still emits some old-style
          links, e.g. prompt editable_in). */}
      <Route path="/studies" element={<Navigate to="/crm/settings/studies" replace />} />
      <Route path="/studies/:id" element={<ParamRedirect build={(p) => `/crm/settings/studies/${p.id}`} />} />
      <Route path="/forms" element={<Navigate to="/crm/settings/forms" replace />} />
      <Route path="/forms/:id" element={<ParamRedirect build={(p) => `/crm/settings/forms/${p.id}`} />} />
      <Route path="/flows" element={<Navigate to="/crm/settings/flows" replace />} />
      <Route path="/flows/:id" element={<ParamRedirect build={(p) => `/crm/settings/flows/${p.id}`} />} />
      <Route path="/templates" element={<Navigate to="/crm/settings/templates" replace />} />
      <Route path="/prompts" element={<Navigate to="/crm/settings/prompts" replace />} />
      <Route path="/prompts/:key" element={<ParamRedirect build={(p) => `/crm/settings/prompts/${p.key}`} />} />
      <Route path="/release-content" element={<Navigate to="/crm/settings/release-content" replace />} />
      <Route path="/fax" element={page(<FaxConsole />)} />
      {/* Old bookmark targets — records triage + the office directory are now
          sub-tabs of the Fax tab. */}
      <Route path="/records" element={<Navigate to="/crm/fax?tab=unmatched" replace />} />
      <Route path="/provider-offices" element={<Navigate to="/crm/fax?tab=offices" replace />} />
      <Route path="/phone" element={page(<PhoneConsole />)} />
      <Route path="/reports" element={page(<Reports />)} />
      <Route path="/contacts/:id" element={page(<ContactDetail />)} />
      {/* Full results page for one AI eligibility review of this lead. */}
      <Route path="/contacts/:id/reviews/:reviewId" element={page(<EligibilityReviewResults />)} />
      <Route path="/system" element={<Navigate to="/crm/settings/system" replace />} />
      {/* In-app handbooks — readable by all CRM users */}
      <Route path="/help" element={page(<Help />)} />
      {/* Catch-all → CRM home */}
      <Route path="*" element={<Navigate to="/crm" replace />} />
    </Routes>
  );
}

/**
 * The floating overlay layer. The call surfaces own their own corners; every
 * notification that interrupts the recruiter — softphone dock included — lives
 * in the one bottom-right OverlayHost with a priority queue behind it.
 *
 * PresenceBeacon is load-bearing, not decoration: hot-lead delivery depends on
 * this user being marked online-in-CRM.
 */
function OverlayMount() {
  return (
    <>
      {/* Full-screen "Call Mode" workspace — z-40, below the call dock;
          auto-opens on connect, mounted once so it survives navigation. */}
      <CallWorkspace />
      {/* Shared parked-calls list (bottom-left, z-40) — pick up from any agent */}
      <ParkedCallsPanel />
      {/* One bottom-right corner: the call dock plus every notification card.
          The SoftphoneBar mounts INSIDE it, in `stack` variant. */}
      <OverlayHost />
      {/* Marks this user online-in-CRM so hot-lead alerts can reach them */}
      <PresenceBeacon />
    </>
  );
}

export function CRMApp() {
  return (
    <CRMErrorBoundary>
      <SoftphoneProvider>
        <CallWorkspaceProvider>
          {/* font-inter scopes the CRM's typeface without touching the other
              platform modules; the fixed overlays live inside it so calls and
              toasts render in the same face as the shell. */}
          <div className="font-inter">
            {/* Promise-based confirm/alert/prompt for the whole module. It wraps
                the overlay hosts as well as the routes, because the softphone,
                the call workspace and the toast hosts raise confirms too — and
                it sits inside font-inter so the dialogs render in the CRM's
                face (they are in-tree, not portalled, for exactly that reason). */}
            <ConfirmProvider>
              {/* "Next up" work sessions live above the routes so the floating
                  bar survives every navigation until the queue is worked dry
                  (docs/crm-work-queue-plan.md). */}
              <WorkQueueProvider>
                <CRMRoutes />
                <NextUpBar />
                {/* Every floating overlay, in whichever shape this user opted into. */}
                <OverlayMount />
              </WorkQueueProvider>
            </ConfirmProvider>
          </div>
        </CallWorkspaceProvider>
      </SoftphoneProvider>
    </CRMErrorBoundary>
  );
}

export default CRMApp;
